<template>
  <div class="p2p-page">
    <div class="p2p-stack" style="gap:6px">
      <h1 class="p2p-h1">Quality review</h1>
      <p class="p2p-lead">Rate anonymised samples of generated posts, only from members who opted in under Settings. Product names, links, emails and handles are hidden, and nothing shows who wrote them.</p>
    </div>
    <div v-if="error" class="p2p-alert bad">{{ error }}</div>

    <section v-if="summary" class="p2p-card" aria-labelledby="quality-summary">
      <h2 id="quality-summary" class="p2p-h2">Last 30 days</h2>
      <div class="p2p-row" style="gap:8px">
        <span class="p2p-chip">{{ summary.reviewed }} reviewed</span>
        <span class="p2p-chip good">{{ summary.verdicts.good }} good</span>
        <span class="p2p-chip warn">{{ summary.verdicts.needs_work }} need work</span>
        <span class="p2p-chip bad">{{ summary.verdicts.unsafe }} unsafe</span>
      </div>
      <table v-if="formats.length" class="p2p-table">
        <thead><tr><th>Format</th><th>Good</th><th>Needs work</th><th>Unsafe</th></tr></thead>
        <tbody><tr v-for="item in formats" :key="item.format">
          <td>{{ formatLabel(item.format) }}</td><td>{{ item.counts.good }}</td><td>{{ item.counts.needs_work }}</td><td>{{ item.counts.unsafe }}</td>
        </tr></tbody>
      </table>
      <div v-if="summary.notes.length" class="p2p-stack" style="gap:4px">
        <span class="p2p-text">Recent notes</span>
        <span v-for="(item, i) in summary.notes" :key="i" class="p2p-faint">{{ formatLabel(item.format) }} · {{ verdictLabel(item.verdict) }}: {{ item.note }}</span>
      </div>
    </section>

    <section class="p2p-stack" aria-labelledby="quality-samples">
      <div class="p2p-between">
        <h2 id="quality-samples" class="p2p-h2">Samples</h2>
        <button type="button" class="p2p-btn sm soft" :disabled="busy" @click="load">New samples</button>
      </div>
      <p v-if="pool" class="p2p-muted" style="margin:0">{{ pool.members }} member{{ pool.members === 1 ? '' : 's' }} opted in · {{ pool.waiting }} post{{ pool.waiting === 1 ? '' : 's' }} waiting for review</p>
      <div v-if="loading" class="p2p-skeleton" style="height:200px"></div>
      <div v-else-if="!samples.length" class="p2p-empty">
        <strong>Nothing to review</strong>
        <span class="p2p-muted">Samples appear when members opt in under Settings and new posts are written.</span>
      </div>
      <article v-for="item in samples" :key="item.sample" class="p2p-card">
        <div class="p2p-row" style="gap:6px">
          <span class="p2p-chip">{{ formatLabel(item.format) }}</span>
          <span class="p2p-chip">{{ angleLabel(item.angle) }}</span>
          <span class="p2p-chip mono">{{ item.week }}</span>
          <span :class="['p2p-chip', item.claims.unsupported ? 'warn' : 'good']">{{ item.claims.supported }} grounded claim{{ item.claims.supported === 1 ? '' : 's' }}<template v-if="item.claims.unsupported"> · {{ item.claims.unsupported }} unconfirmed</template></span>
        </div>
        <h3 class="p2p-h3" style="margin:0">{{ item.hook }}</h3>
        <p class="p2p-text" style="margin:0;white-space:pre-wrap">{{ item.caption }}</p>
        <div v-if="item.headline" class="p2p-faint">Picture headline: {{ item.headline }}</div>
        <ol v-if="item.slides.length" class="p2p-facts">
          <li v-for="(slide, i) in item.slides" :key="i" class="p2p-text"><strong>{{ slide.headline }}</strong><span v-if="slide.body"> · {{ slide.body }}</span></li>
        </ol>
        <div v-if="item.scenes.length" class="p2p-faint">Video scenes: {{ item.scenes.join(' / ') }}</div>
        <details v-if="item.thread.length"><summary class="p2p-faint">Thread version ({{ item.thread.length }} parts)</summary>
          <p v-for="(part, i) in item.thread" :key="i" class="p2p-text" style="white-space:pre-wrap">{{ part }}</p>
        </details>
        <label class="p2p-label">Note (optional, kept with the verdict)
          <input v-model="notes[item.sample]" class="p2p-input" maxlength="500" placeholder="What would make it better?">
        </label>
        <div class="p2p-row" style="gap:8px">
          <button type="button" class="p2p-btn sm" :disabled="busy" @click="rate(item, 'good')">Good</button>
          <button type="button" class="p2p-btn sm soft" :disabled="busy" @click="rate(item, 'needs_work')">Needs work</button>
          <button type="button" class="p2p-btn sm ghost" :disabled="busy" @click="rate(item, 'unsafe')">Unsafe</button>
        </div>
      </article>
    </section>
  </div>
</template>

<script>
import { get, post, message } from '../api';
import { GOAL_LABELS } from '../goals';

const FORMATS = { text: 'Text', image: 'Image', carousel: 'Carousel', video: 'Video' };
const VERDICTS = { good: 'Good', needs_work: 'Needs work', unsafe: 'Unsafe' };

export default {
  name: 'AdminQuality',
  data: () => ({ samples: [], summary: null, pool: null, notes: {}, loading: true, busy: false, error: '' }),
  computed: {
    formats() { return this.summary ? Object.keys(this.summary.by_format).map(format => ({ format, counts: this.summary.by_format[format] })) : []; }
  },
  mounted() { this.load(); },
  methods: {
    formatLabel(value) { return FORMATS[value] || value; },
    angleLabel(value) { return GOAL_LABELS[value] || value; },
    verdictLabel(value) { return VERDICTS[value] || value; },
    async load() {
      this.loading = true;
      this.error = '';
      try {
        const [samples, summary] = await Promise.all([get('/admin/quality/sample', { limit: 6 }), get('/admin/quality/summary')]);
        this.samples = samples.samples;
        this.pool = { members: samples.members_opted_in, waiting: samples.waiting };
        this.summary = summary;
      } catch (error) {
        this.error = message(error);
      } finally {
        this.loading = false;
      }
    },
    async rate(item, verdict) {
      this.busy = true;
      this.error = '';
      try {
        this.summary = await post('/admin/quality/reviews', { sample: item.sample, verdict, note: (this.notes[item.sample] || '').trim() });
        this.samples = this.samples.filter(sample => sample.sample !== item.sample);
        if (this.pool) this.pool.waiting = Math.max(0, this.pool.waiting - 1);
      } catch (error) {
        this.error = message(error);
      } finally {
        this.busy = false;
      }
    }
  }
};
</script>
